import { TemplateErrorComponent } from "@/components/error";
import { PendingTemplateComponent } from "@/components/pending";
import GrapesJSEditor from "@papier/grapesjs-editor";
import { UmoEditor } from "@/components/editors/UmoEditor";
import { Button } from "@papier/ui/components/ui/button";
import { Card, CardContent } from "@papier/ui/components/ui/card";
import { createFileRoute, notFound, useRouter } from "@tanstack/react-router";
import { ParentSize } from "@visx/responsive";
import { ArrowLeft, Zap } from "lucide-react";
import { useMemo, useState } from "react";
import { useCollab, useY } from "@papier/grapesjs-editor/src/hooks/use-collab";
import { PublishDialog } from "@/components/template/publish-dialog";
import { LanguageSwitcher } from "@/components/template/language-switcher";
import type { LocaleEnum } from "@papier/shared";
import { useSuspenseQuery } from "@tanstack/react-query";
import { trpc } from "@/lib/trpc";
import { createMediaGalleryIntegration } from "@/lib/media-gallery-integration";

export const Route = createFileRoute("/_app/projects/$projectSlug/templates/$templateSlug/edit")({
  component: EditTemplate,
  wrapInSuspense: true,
  pendingComponent: () => <PendingTemplateComponent />,
  errorComponent: ({ error }) => <TemplateErrorComponent error={error} />,
  loader: async ({ params: { projectSlug, templateSlug }, context: { queryClient, trpc } }) => {
    const projectData = await queryClient.ensureQueryData(
      trpc.projects.getBySlug.queryOptions({ projectSlug }),
    );
    if (!projectData) {
      notFound();
    }

    const templateData = await queryClient.ensureQueryData(
      trpc.templates.getDraft.queryOptions({
        projectId: projectData.id,
        templateSlug,
      }),
    );

    return { projectData, templateData };
  },
  head: ({ loaderData }) => ({
    meta: [{ title: `Papier - ${loaderData?.templateData.name || "Template"}` }],
  }),
});

const tryJsonParse = (value: string) => {
  try {
    return JSON.parse(value);
  } catch {
    return value;
  }
};

function EditTemplate() {
  const { Link, useRouteContext, useLoaderData, useParams } = Route;
  const router = useRouter();
  const { authOptions } = useRouteContext();
  const { projectData } = useLoaderData();
  const { templateSlug } = useParams();

  const { data: user } = useSuspenseQuery(authOptions.session);

  const { data: template } = useSuspenseQuery(
    trpc.templates.getDraft.queryOptions({ templateSlug, projectId: projectData.id }),
  );

  const availableLocales = template.content?.locales || [];
  const availableLocaleCodes = useMemo(
    () => availableLocales.map((locale) => locale.locale),
    [availableLocales],
  );

  const [selectedLocaleCode, setSelectedLocaleCode] = useState<LocaleEnum>(
    availableLocales[0]!.locale,
  );

  const selectedLocale = availableLocales.find((locale) => locale.locale === selectedLocaleCode)!;

  const handleLocaleChange = (newLocaleCode: LocaleEnum) => {
    if (newLocaleCode === selectedLocaleCode) return;
    setSelectedLocaleCode(newLocaleCode);
  };

  const content = selectedLocale.content as
    | {
        html: string;
        css: string;
      }
    | undefined;

  const { ydoc, onUpdate, awareness } = useCollab({
    documentId: selectedLocale.id,
    user: {
      id: user!.user.id,
      name: user!.user.name,
    },
    initialData: {
      html: content?.html || "<div>Your content here</div>",
      css: content?.css || "{}",
    },
  });

  const { ydoc: ydocSchema, onUpdate: onUpdateSchema } = useCollab({
    documentId: template.content!.templateSchemaId,
    user: {
      id: user!.user.id,
      name: user!.user.name,
    },
    initialData: {
      schema: JSON.stringify(template.schema?.schemaDefinition || { schema: [] }),
    },
  });

  const components = useY(ydoc.getText("html"));
  const style = useY(ydoc.getText("css"));
  const schema = useY(ydocSchema.getText("schema"));

  // Create media gallery integration
  const mediaGalleryConfig = useMemo(
    () => createMediaGalleryIntegration(projectData.id, selectedLocale.id),
    [projectData.id, selectedLocale.id],
  );

  if (!components || !style || !schema) {
    return null;
  }

  return (
    <main className="fixed inset-0 z-50 flex flex-col">
      <Card className="rounded-none border-0 py-4">
        <CardContent className="flex justify-between gap-10 px-4">
          <div>
            {template.status === "published" ? (
              <Button
                onClick={() => router.history.back()}
                title="Back"
                variant="outline"
                className="cursor-default"
              >
                <ArrowLeft className="size-5" />
                Back
              </Button>
            ) : (
              <Button title="Project" variant="outline" asChild className="cursor-default">
                <Link to="/projects/$projectSlug">
                  <ArrowLeft className="size-5" />
                  Project
                </Link>
              </Button>
            )}
          </div>

          <div className="flex items-center">
            <LanguageSwitcher
              currentLocale={selectedLocaleCode}
              availableLocales={availableLocaleCodes}
              onChange={handleLocaleChange}
            />
          </div>

          <div className="flex flex-wrap items-center justify-end gap-2">
            <PublishDialog>
              <Button variant="outline">
                <Zap />
                <span className="hidden sm:inline">Publish</span>
              </Button>
            </PublishDialog>
          </div>
        </CardContent>
      </Card>
      <div className="min-h-0 flex-1">
        <ParentSize>
          {({ height }) => (
            <>
              {template.type === "email" ? (
                <GrapesJSEditor
                  height={height}
                  awareness={awareness}
                  onUpdate={onUpdate}
                  initialContent={{
                    components: tryJsonParse(components),
                    style: tryJsonParse(style),
                  }}
                  initialSchema={JSON.parse(schema)}
                  onSchemaChange={(newSchema) => {
                    onUpdateSchema(JSON.stringify(newSchema), "schema");
                  }}
                  mediaGallery={mediaGalleryConfig}
                />
              ) : template.type === "document" ? (
                <UmoEditor
                  schema={JSON.parse(schema)}
                  onSchemaChange={(newSchema) => {
                    onUpdateSchema(JSON.stringify(newSchema), "schema");
                  }}
                  mediaGallery={mediaGalleryConfig}
                />
              ) : (
                <div className="flex h-full items-center justify-center text-center">
                  <div>
                    <h2 className="mb-2 text-lg font-semibold">Unsupported Template Type</h2>
                    <p className="text-gray-600">
                      The template type "{template.type}" is not yet supported for editing.
                    </p>
                  </div>
                </div>
              )}
            </>
          )}
        </ParentSize>
      </div>
    </main>
  );
}
